<script lang="ts" setup>
import ArrowDown24Icon from '~/assets/icons/arrows/arrowDown24.svg?component'
import {
  onAccordionTransitionEnter,
  onAccordionTransitionLeave,
  onAccordionAfterEnter,
} from '~/components/ui/accordion/accordion-script'
import type { IAccordionItem } from '~~/shared/interfaces/ui/accordion'

const props = defineProps<{
  accordionItem: IAccordionItem
  accordionColor?: 'gray' | 'transparent'
  accordionSize?: 'big'
  accordionPosition?: number
  hideArrow?: boolean
  hasPlusIcon?: boolean
  arrowIcon?: Component
  isEllipsisTitle?: boolean
  selfState?: boolean
}>()

const emits = defineEmits(['setAccordionSectionStatus'])

const accordionIsOpen = ref(false)

accordionIsOpen.value = props.accordionItem.isOpen

const getAccordionShowStatus = computed(() => {
  if (props.selfState) {
    return accordionIsOpen.value
  }

  return props.accordionItem.isOpen
})

function setAccordionSectionStatus(
  value: string | number | undefined,
  isOpen: boolean,
  isBorder?: boolean
) {
  if (!props.selfState) {
    emits('setAccordionSectionStatus', {
      accordionPosition: props.accordionPosition || 0,
      value,
      isOpen,
      isBorder,
    })
  } else {
    accordionIsOpen.value = isOpen
  }
}


</script>

<template>
  <div
    class="accordion"
    :class="[
      hasPlusIcon && 'accordion_with-pluses',
      accordionItem.isOpen && 'active',
    ]"
  >
    <div class="accordion-item">
      <div
        @click="
          setAccordionSectionStatus(
            accordionItem.value,
            !getAccordionShowStatus,
            accordionItem?.isBorder
          )
        "
        class="accordion-item__header svg-fill-black"
        :class="[
          accordionColor,
          accordionSize,
          accordionItem?.isBorder && 'accordion-item__header-border',
        ]"
      >
        <slot name="accordionHeader" />
        <div
          v-if="accordionItem.title"
          class="accordion-item__header-title"
          :class="isEllipsisTitle && 'accordion-item__header-title_ellipsis'"
        >
          {{ accordionItem.title }}
        </div>
        <div
          class="accordion__plus"
          :class="getAccordionShowStatus && 'active'"
          v-if="hasPlusIcon"
        >
          <div class="accordion__plus-line"></div>
          <div class="accordion__plus-line"></div>
        </div>
        <div class="accordion__icon">
          <ArrowDown24Icon
            v-if="!hasPlusIcon && !arrowIcon"
            class="accordion-item__header-icon"
            :class="getAccordionShowStatus && 'active'"
          />
          <component
            v-if="arrowIcon && !hasPlusIcon"
            class="accordion-item__header-icon"
            :class="getAccordionShowStatus && 'active'"
            :is="arrowIcon"
          ></component>
        </div>
      </div>
      <Transition
        name="accordion-item"
        @enter="onAccordionTransitionEnter"
        @leave="onAccordionTransitionLeave"
        @after-enter="onAccordionAfterEnter"
      >
        <div v-show="getAccordionShowStatus" class="accordion-item__content">
          <slot name="accordionContent" />
        </div>
      </Transition>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.accordion {
  &_with-pluses {
    .accordion__plus {
      width: 20px;
      height: 20px;
      position: relative;
      transition: transform 0.3s;

      &.active {
        transform: rotate(360deg);

        .accordion__plus-line {
          &:first-child {
            opacity: 0;
          }
        }
      }

      &-line {
        background-color: $bg_black;
        position: absolute;
        border-radius: 3px;
        transition: opacity 0.3s;

        &:first-child {
          width: 2px;
          height: 100%;
          left: 50%;
          top: 0;
          transform: translateX(-50%);
        }

        &:last-child {
          width: 100%;
          height: 2px;
          left: 0;
          top: 50%;
          transform: translateY(-50%);
        }
      }
    }
    .accordion-item {
      &__header {
        padding: 16px 0;
        position: relative;
        &-title {
          color: $content_primary;
        }
      }
    }
  }

  &-item {
    margin-top: 28px;
    &:first-child {
      margin-top: 0;
    }

    &__header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      background: #383a40;
      padding: 10px 12px;
      border-radius: 8px;
      cursor: pointer;
      gap: 16px;
      &.big {
        padding: 12px 16px;
        @include sm-breakpoint {
          padding: 8px 12px;
        }
      }
      &-icon {
        transform: rotate(0);
        transition: 0.3s transform;
        &.active {
          transform: rotate(180deg);
        }
      }

      &.gray {
        background: $control-bg_neutral;
        .accordion-item__header-title {
          color: $content_primary;
        }
      }

      &.transparent {
        background-color: transparent;
      }

      &-title {
        color: $content_white;
        @include font(18px, 22px, 600);

        &_ellipsis {
          text-overflow: ellipsis;
          white-space: nowrap;
          max-width: calc(100% - 40px);
          overflow: hidden;
        }
      }
      &-border {
        position: relative;
        overflow: hidden;
        &::before {
          content: '';
          position: absolute;
          display: block;
          width: 5px;
          height: 100%;
          left: 0;
          background: $content_success;
        }
      }
    }
  }
}
</style>
